import {
  Dialog,
  DialogVariant,
  SnackbarVariant,
  Text,
  UseSnackbar,
  useSnackbar
} from '@focaldata/cin-ui-components'
import React, { useMemo, useState } from 'react'
import { draftQuestionnaireRefetchQuery } from '../../../data/gql/questionnaire/queries'
import { GET_AUDIENCES } from '../../../data/gql/questionnaire/queries/audiences'
import { GET_CUSTOM_CRITERIA } from '../../../data/gql/questionnaire/queries/customCriteria'
import { fieldworkRefetchQuery } from '../../../data/gql/questionnaire/queries/fieldwork'
import { useUpdateFieldworkV2 } from '../../../hooks/audience/createOrUpdateFieldworkV2'
import { useAudienceCountries } from '../../../hooks/audience/useAudienceCountries'
import {
  CUSTOM_AUDIENCE_PRESET_NAME,
  useGetPresetAudiencesForLanguages
} from '../../../hooks/audience/usePresetAudience'
import { useFieldwork } from '../../../hooks/useFieldwork'
import { useIsAudienceReadOnly } from '../../../hooks/useIsAudienceReadOnly'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import {
  PanelSupplier,
  convertAudienceType
} from '../../../utils/audienceHelper'
import { getCustomAudienceSelectItem } from '../utils'
import LanguageAllocationsControl, {
  AllocationRow
} from './LanguageAllocations.control'
import {
  addLanguage,
  changeLanguage,
  changePercent,
  nextAvailableLanguage,
  removeLanguage,
  supportedLanguagesForCountry
} from './LanguageAllocations.utils'

const LanguageAllocations: React.FC = () => {
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const projectId = useProjectId()
  const { isReadOnly } = useIsAudienceReadOnly()
  const { fieldwork } = useFieldwork()
  // Both call sites gate this on PureSpectrum, but the catalogue and the write
  // are supplier-specific — a hardcode would outlive either gate.
  const audienceType = fieldwork?.audience[0]?.__typename
  const panelSupplierCode = audienceType
    ? convertAudienceType(audienceType)
    : PanelSupplier.PURESPECTRUM
  const { data: countries } = useAudienceCountries(panelSupplierCode)
  const { updateFieldworkV2 } = useUpdateFieldworkV2()
  const { enqueueSnackbar }: UseSnackbar = useSnackbar()

  const country = fieldwork?.locale.country
  const allocations: AllocationRow[] = useMemo(() => {
    const persisted =
      fieldwork?.languageAllocations?.map(({ language, percent }) => ({
        language,
        percent
      })) ?? []
    if (persisted.length > 0) return persisted
    // A market with no stored allocations (e.g. a single-language market, or
    // one created before language allocations existed) still has a primary
    // locale language. Show it as a single 100% row so the researcher sees the
    // market's language up front, rather than an empty "+ Add language" that
    // only reveals the default language after a click. Display-only — matches
    // the launch/read fallback in useMarketsCompletes.
    const primary = fieldwork?.locale.language
    return primary ? [{ language: primary, percent: 1 }] : []
  }, [fieldwork?.languageAllocations, fieldwork?.locale.language])

  const supportedLanguages = useMemo(
    () => supportedLanguagesForCountry(countries, country),
    [countries, country]
  )

  // Which language the applied preset audience belongs to (undefined for
  // Custom / none / while unresolved), resolved against the same per-language
  // union the preset pickers use. Dropping that language makes the preset
  // inapplicable, so it needs the confirm-and-revert flow below.
  const appliedPreset = fieldwork?.presetAudience
  const hasNonCustomPreset =
    !!appliedPreset && appliedPreset.name !== CUSTOM_AUDIENCE_PRESET_NAME
  const allocationLanguages = useMemo(
    () => allocations.map(({ language }) => language),
    [allocations]
  )
  const { data: presets, loading: loadingPresets } =
    useGetPresetAudiencesForLanguages(
      hasNonCustomPreset ? country || '' : '',
      allocationLanguages
    )
  const appliedPresetLanguage = presets.find(
    ({ presetAudienceId }) =>
      presetAudienceId === appliedPreset?.presetAudienceId
  )?.language
  // While the preset lists are still loading, the applied preset's language
  // is unknown — the confirm-and-revert guard below couldn't fire, so a
  // removal in that window would silently strand the preset. Hold edits
  // until it resolves (cold cache only; warm-cache reads are synchronous).
  const presetLanguageUnresolved = hasNonCustomPreset && loadingPresets

  // Allocations awaiting the "revert audience to Custom" confirmation.
  const [pendingRemoval, setPendingRemoval] = useState<AllocationRow[] | null>(
    null
  )

  const commit = async (
    next: AllocationRow[],
    revertAudienceToCustom = false
  ) => {
    // samplesNeeded is the survey-level total and is owned by the top
    // "Completes" input — we never touch it from here. We only persist the
    // per-language percentages, which the backend validates (each in (0,1],
    // summing to 1.0) and uses to derive per-language sample counts.
    try {
      const result = await updateFieldworkV2({
        variables: {
          input: {
            surveyId,
            panelSupplierCode,
            // Tri-state optional field on UpdateFieldworkV2Input — the list of
            // allocations goes under `value`.
            languageAllocations: {
              value: next.map(({ language, percent }) => ({
                language,
                percent
              }))
            },
            // Confirmed removal of the applied preset's language: revert the
            // audience to Custom in the same write.
            ...(revertAudienceToCustom
              ? {
                  presetAudienceId:
                    getCustomAudienceSelectItem(presets).value || undefined
                }
              : {})
          }
        },
        refetchQueries: [
          fieldworkRefetchQuery(surveyId),
          draftQuestionnaireRefetchQuery(projectId, surveyId, waveId),
          // The revert replaces the audience members server-side; re-read the
          // criteria wherever they are mounted (document-form entries only
          // refetch active queries).
          ...(revertAudienceToCustom
            ? [GET_AUDIENCES, GET_CUSTOM_CRITERIA]
            : [])
        ]
      })
      // errorPolicy is Apollo's default ('none'), so GraphQL/network errors
      // reject the promise and land in the catch below — not here. This
      // branch only covers the partial-error shape (errors returned on the
      // result alongside data), kept so a future errorPolicy change still
      // surfaces a message.
      if (result.errors?.length) {
        enqueueSnackbar(result.errors[0].message, {
          variant: SnackbarVariant.Error
        })
      }
    } catch (error) {
      // The hook's onError already logs to the tracker (captureApolloError);
      // this surfaces the failure to the researcher. Without it the rejected
      // promise is swallowed and the rebalanced percentages silently fail to
      // persist — the backend rejects any split that doesn't sum to 1.0.
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : 'Failed to update language allocations',
        { variant: SnackbarVariant.Error }
      )
    }
  }

  const nextLanguage = nextAvailableLanguage(allocations, supportedLanguages)

  // Removing or replacing the language the applied preset belongs to makes
  // the preset inapplicable: hold the change and confirm that the audience
  // will be reverted to Custom (product decision — see the dialog below).
  const commitAllocations = (next: AllocationRow[]) => {
    // Belt and braces with the disabled control below: while the preset's
    // language is unresolved, any change that drops a currently-fielded
    // language could be the guarded one — refuse it rather than let it slip
    // past the confirm dialog.
    const dropsAnyLanguage = allocations.some(
      ({ language }) => !next.some((row) => row.language === language)
    )
    if (presetLanguageUnresolved && dropsAnyLanguage) {
      return
    }
    const dropsPresetLanguage =
      !!appliedPresetLanguage &&
      allocations.some(({ language }) => language === appliedPresetLanguage) &&
      next.every(({ language }) => language !== appliedPresetLanguage)
    if (dropsPresetLanguage) {
      setPendingRemoval(next)
      return
    }
    commit(next)
  }

  const handleAdd = () => {
    // The survey-level samplesNeeded total is untouched, so per-language
    // sample counts simply re-divide the same total.
    if (!nextLanguage) return
    commitAllocations(addLanguage(allocations, nextLanguage))
  }

  const handleRemove = (index: number) => {
    commitAllocations(removeLanguage(allocations, index))
  }

  const handleChangeLanguage = (index: number, language: string) => {
    commitAllocations(changeLanguage(allocations, index, language))
  }

  const handleChangePercent = (index: number, newPercentInt: number) => {
    // A single allocation is always 100% — nothing to rebalance, so skip the
    // redundant backend write.
    if (allocations.length <= 1) return
    commitAllocations(changePercent(allocations, index, newPercentInt))
  }

  const presetLanguageName =
    supportedLanguages.find(({ code }) => code === appliedPresetLanguage)
      ?.name ?? appliedPresetLanguage

  return (
    <>
      <LanguageAllocationsControl
        allocations={allocations}
        supportedLanguages={supportedLanguages.map((l) => ({
          id: l.code,
          name: l.name,
          value: l.code
        }))}
        disabled={isReadOnly || presetLanguageUnresolved}
        onAdd={handleAdd}
        onRemove={handleRemove}
        onChangeLanguage={handleChangeLanguage}
        onChangePercent={handleChangePercent}
      />
      <Dialog
        variant={DialogVariant.DoubleButton}
        title="Remove preset audience?"
        open={pendingRemoval !== null}
        primaryButtonText="Remove language"
        primaryButtonClick={async () => {
          if (pendingRemoval) {
            await commit(pendingRemoval, true)
          }
          setPendingRemoval(null)
        }}
        secondaryButtonText="Cancel"
        secondaryButtonClick={() => setPendingRemoval(null)}
        onClose={() => setPendingRemoval(null)}
      >
        <Text>
          The audience preset “{appliedPreset?.name}” belongs to{' '}
          {presetLanguageName}. Removing this language will revert the market’s
          audience to Custom — the preset’s targeting and screening will be
          removed.
        </Text>
      </Dialog>
    </>
  )
}

export default LanguageAllocations
